/**
 * Query keys mirror the URL, so a key can be read off the address bar.
 *
 * The lifecycle coupling is expressed here once rather than rediscovered per screen: correcting
 * a date on the Overview tab regenerates both rule chains, so a lifecycle write invalidates the
 * register and every matter read under it (§3.3, `pages.md` §3.1).
 */

export const keys = {
  meta: ['meta'] as const,
  authConfig: ['auth-config'] as const,

  jurisdictions: (params: Record<string, string | number | undefined>) =>
    ['jurisdictions', params] as const,
  jurisdiction: (code: string, axes: Record<string, string | undefined> = {}) =>
    ['jurisdiction', code, axes] as const,
  jurisdictionAll: (code: string) => ['jurisdiction', code] as const,
  successions: (code: string) => ['successions', code] as const,

  matter: (code: string, matter: string, params: Record<string, string | undefined>) =>
    ['matter', code, matter, params] as const,
  matterAll: (code: string) => ['matter', code] as const,

  ruleHistory: (code: string, matter: string, lane: string, recordedAt?: string) =>
    ['rule-history', code, matter, lane, recordedAt ?? null] as const,

  sources: (code: string, matter: string) => ['sources', code, matter] as const,
  gaps: (code: string, matter: string) => ['gaps', code, matter] as const,
  coverage: (matter: string) => ['coverage', matter] as const,
  runImpact: (runId: string) => ['run-impact', runId] as const,
  pendingChanges: (code: string, matter: string) => ['pendingChanges', code, matter] as const,
  // A prefix and a variant, kept apart. `invalidateQueries` matches by prefix, so invalidating
  // the variant that happens to be false reaches no cache holding the true one — which is every
  // cache the admin screen fills.
  users: () => ['users'] as const,
  usersScoped: (includeSuspended: boolean) => ['users', includeSuspended] as const,
  exports: () => ['exports'] as const,

  queue: (matter?: string) => ['queue', matter ?? 'all'] as const,
  queueEntry: (id: string) => ['queue', 'entry', id] as const,

  proposals: (matter?: string) => ['proposals', matter ?? 'all'] as const,
  proposal: (id: string) => ['proposal', id] as const,

  prompts: ['prompts'] as const,
  promptVersions: (id: string) => ['prompt-versions', id] as const,

  contradictions: (params: Record<string, string | undefined>) =>
    ['contradictions', params] as const,

  mappings: () => ['mappings'] as const,
  importRows: (id: string) => ['import-rows', id] as const,

  ruleCatalogue: (params: Record<string, string | undefined>) =>
    ['rule-catalogue', params] as const,

  runs: (params: Record<string, string | number | undefined>) => ['runs', params] as const,
  run: (id: string) => ['run', id] as const,
  runEvents: (id: string) => ['run-events', id] as const,
} as const;
